/**
 * Biometric (Face ID / Touch ID / fingerprint) quick-login.
 *
 * After a successful password or social login we stash the session token in the
 * OS keychain (`expo-secure-store`). On a later visit the user can unlock it with
 * a biometric prompt (`expo-local-authentication`) instead of retyping their
 * password — the stored `{ token, user }` is fed straight into `continueAfterLogin`.
 */

import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import AsyncStorage from '@react-native-async-storage/async-storage';

const BIO_TOKEN_KEY = 'HG_BIOMETRIC_TOKEN';
const BIO_USER_KEY = 'HG_BIOMETRIC_USER';

/** @returns {Promise<boolean>} device has biometric hardware AND an enrolled biometric. */
export async function isBiometricAvailable() {
    try {
        const [hasHardware, enrolled] = await Promise.all([
            LocalAuthentication.hasHardwareAsync(),
            LocalAuthentication.isEnrolledAsync(),
        ]);

        return !!hasHardware && !!enrolled;
    } catch (_e) {
        return false;
    }
}

/** @returns {Promise<'faceId'|'fingerprint'|'biometric'>} the enrolled biometric kind. */
export async function getBiometricKind() {
    try {
        const types = await LocalAuthentication.supportedAuthenticationTypesAsync();

        if (types.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
            return 'faceId';
        }
        if (types.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
            return 'fingerprint';
        }

        return 'biometric';
    } catch (_e) {
        return 'biometric';
    }
}

/**
 * Prompt the OS biometric sheet.
 * @param {string} promptMessage
 * @returns {Promise<boolean>} whether the user authenticated.
 */
export async function authenticateBiometric(promptMessage) {
    try {
        const res = await LocalAuthentication.authenticateAsync({
            promptMessage,
            disableDeviceFallback: false,
        });

        return !!res?.success;
    } catch (_e) {
        return false;
    }
}

/**
 * Persist the session for future biometric login (keychain-backed).
 * @param {string} token
 * @param {object} [user]
 * @returns {Promise<boolean>}
 */
export async function enableBiometricLogin(token, user) {
    try {
        if (!token) {
            return false;
        }
        await SecureStore.setItemAsync(BIO_TOKEN_KEY, String(token));
        if (user) {
            const serialized = JSON.stringify(user);

            // SecureStore caps values at 2048 bytes; a full user profile often exceeds it.
            if (serialized.length <= 2000) {
                await SecureStore.setItemAsync(BIO_USER_KEY, serialized);
                await AsyncStorage.removeItem(BIO_USER_KEY).catch(() => {});
            } else {
                await SecureStore.deleteItemAsync(BIO_USER_KEY);
                await AsyncStorage.setItem(BIO_USER_KEY, serialized);
            }
        }

        return true;
    } catch (_e) {
        return false;
    }
}

/** @returns {Promise<boolean>} whether a session is stored for biometric login. */
export async function isBiometricLoginEnabled() {
    try {
        return !!(await SecureStore.getItemAsync(BIO_TOKEN_KEY));
    } catch (_e) {
        return false;
    }
}

/** @returns {Promise<{token:string, user:object|null}|null>} the stored session. */
export async function getBiometricSession() {
    try {
        const token = await SecureStore.getItemAsync(BIO_TOKEN_KEY);

        if (!token) {
            return null;
        }
        const userRaw = (await SecureStore.getItemAsync(BIO_USER_KEY)) || (await AsyncStorage.getItem(BIO_USER_KEY));

        return { token, user: userRaw ? JSON.parse(userRaw) : null };
    } catch (_e) {
        return null;
    }
}

/** Forget the stored biometric session (e.g. on logout). */
export async function disableBiometricLogin() {
    try {
        await SecureStore.deleteItemAsync(BIO_TOKEN_KEY);
        await SecureStore.deleteItemAsync(BIO_USER_KEY);
        await AsyncStorage.removeItem(BIO_USER_KEY);
    } catch (_e) {
        // ignore
    }
}

// ── Biometric 2FA (Face ID / Touch ID / passcode as a second factor) ─────────
// A *server-issued* device-trust token (see POST /api/auth/2fa/device-trust/issue)
// kept in the OS keychain behind a biometric gate. Unlike the plain "remember
// this device" token (AsyncStorage, silent bypass), this one is released only
// after a successful local biometric prompt — so Face ID / Touch ID / passcode
// acts as the second factor at login. The token is bound to the user + device on
// the server, so it is inert for any other account on the same device.
const BIO_DEVICE_TRUST_KEY = 'HG_BIO_DEVICE_TRUST';

/**
 * Enrol biometric 2FA on this device by stashing the device-trust token behind
 * the keychain/biometric gate.
 * @param {string} token - device-trust token from `/api/auth/2fa/device-trust/issue`.
 * @returns {Promise<boolean>}
 */
export async function setBiometricDeviceTrust(token) {
    try {
        if (!token) {
            return false;
        }
        await SecureStore.setItemAsync(BIO_DEVICE_TRUST_KEY, String(token));

        return true;
    } catch (_e) {
        return false;
    }
}

/** @returns {Promise<string|null>} the stored biometric-gated device-trust token. */
export async function getBiometricDeviceTrust() {
    try {
        return await SecureStore.getItemAsync(BIO_DEVICE_TRUST_KEY);
    } catch (_e) {
        return null;
    }
}

/** @returns {Promise<boolean>} whether biometric 2FA is enrolled on this device. */
export async function isBiometricTwoFactorEnabled() {
    try {
        return !!(await SecureStore.getItemAsync(BIO_DEVICE_TRUST_KEY));
    } catch (_e) {
        return false;
    }
}

/** Disable biometric 2FA on this device (forget the gated device-trust token). */
export async function clearBiometricDeviceTrust() {
    try {
        await SecureStore.deleteItemAsync(BIO_DEVICE_TRUST_KEY);
    } catch (_e) {
        // ignore
    }
}
